import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, AlertCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@services/api';

const maskEmail = (emailStr: string) => {
  if (!emailStr || !emailStr.includes('@')) return emailStr;
  const [user, domain] = emailStr.split('@');

  const maskString = (str: string) => {
    if (str.length <= 1) return str + '*';
    if (str.length === 2) return `${str[0]}*${str[1]}`;
    const first = str[0];
    const last = str[str.length - 1];
    const stars = '*'.repeat(Math.max(3, str.length - 2));
    return `${first}${stars}${last}`;
  };

  const maskedUser = maskString(user);

  let maskedDomain = domain;
  if (domain && domain.includes('.')) {
    const parts = domain.split('.');
    const mainDomain = parts[0];
    const ext = parts.slice(1).join('.');
    maskedDomain = `${maskString(mainDomain)}.${ext}`;
  }

  return `${maskedUser}@${maskedDomain}`;
};

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const { mutate: forgotPassword, isPending } = useMutation({
    mutationFn: (email: string) => authApi.forgotPassword(email),
    onSuccess: () => {
      setErrorMsg('');
      setSubmitted(true);
    },
    onError: (err: any) => {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'No registered user found with this email address. Please check your email.';
      setErrorMsg(msg);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    if (email.trim()) {
      forgotPassword(email);
    }
  };

  if (submitted) {
    return (
      <div className="text-center animate-fade-in">
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Mail className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Check your inbox</h2>
        <p className="text-muted-foreground text-sm mb-6">
          A password reset link has been successfully sent to <strong>{maskEmail(email)}</strong>.
        </p>
        <Link
          to="/login"
          className="text-sm text-primary hover:underline flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <h2 className="text-2xl font-bold text-foreground mb-1">Forgot password?</h2>
      <p className="text-muted-foreground text-sm mb-6">
        Enter your registered email address and we'll send a reset link.
      </p>

      {errorMsg && (
        <div className="p-3.5 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-xs flex items-start gap-2.5 mb-4 animate-fade-in">
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">User Registration Check Failed</p>
            <p className="mt-0.5 opacity-90">{errorMsg}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">Email address</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          type="submit"
          disabled={isPending}
          className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60"
        >
          {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          {isPending ? 'Sending...' : 'Send Reset Link'}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        <Link
          to="/login"
          className="text-primary hover:underline flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </Link>
      </p>
    </div>
  );
}
